import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  DialogContentText,
  Divider,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { evaluateSupplierClaims } from '../../services/procurementService';
import { unifiedStorageService } from '../../services/unifiedStorageService';
import type {
  EvaluationScore,
  Procurement,
  ProcurementSettings,
  Rubric,
  RubricCriteria,
  Supplier,
} from '../../types/procurement';
import { BaseModal } from '../common/UI/BaseModal';

interface Props {
  supplier: Supplier;
  rubric: Rubric;
  procurement: Procurement;
  initialScores?: EvaluationScore[];
  initialOverallComments?: string;
  isSubmitted?: boolean;
  onSaveEvaluation: (
    scores: EvaluationScore[],
    overallComments: string,
    isAutoSave?: boolean,
    status?: 'DRAFT' | 'SUBMITTED'
  ) => Promise<boolean | undefined> | undefined;
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const SupplierAssessmentMatrix: React.FC<Props> = ({
  supplier,
  rubric,
  procurement,
  initialScores = [],
  initialOverallComments = '',
  isSubmitted = false,
  onSaveEvaluation,
  onShowSnackbar,
}) => {
  const { account } = useAccount();
  const [scores, setScores] = useState<EvaluationScore[]>(initialScores);
  const [overallComments, setOverallComments] = useState<string>(initialOverallComments);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [localSubmitted, setLocalSubmitted] = useState(false);

  const isInitialMount = useRef(true);
  const onSaveRef = useRef(onSaveEvaluation);

  // Sync incoming props to local state if they change significantly (e.g., from DB after background AI task)
  useEffect(() => {
    // Only overwrite local state if we aren't actively typing/modifying
    // In practice, this ensures the background AI completion pushes to UI without a refresh
    if (!isInitialMount.current && !localSubmitted && !isEvaluating) {
      setScores((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(initialScores)) {
          return prev;
        }
        return initialScores;
      });
      setOverallComments((prev) => {
        if (prev === initialOverallComments) {
          return prev;
        }
        return initialOverallComments;
      });
    }
  }, [initialScores, initialOverallComments, localSubmitted, isEvaluating]);

  useEffect(() => {
    onSaveRef.current = onSaveEvaluation;
  }, [onSaveEvaluation]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!isSubmitted && !localSubmitted) {
        onSaveRef.current(scores, overallComments, true, 'DRAFT');
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [scores, overallComments, isSubmitted, localSubmitted]);

  const handleConfirmSubmit = async () => {
    setLocalSubmitted(true);
    setConfirmOpen(false);

    try {
      const success = await onSaveEvaluation(scores, overallComments, false, 'SUBMITTED');
      if (success === false) {
        setLocalSubmitted(false);
      }
    } catch (_error) {
      setLocalSubmitted(false);
    }
  };

  const handleDownloadDocuments = async () => {
    if (!supplier.documents || supplier.documents.length === 0) {
      onShowSnackbar?.('No documents available to download.', 'error');
      return;
    }

    onShowSnackbar?.(`Downloading ${supplier.documents.length} document(s)...`, 'success');

    for (const doc of supplier.documents) {
      if (doc.url) {
        try {
          await unifiedStorageService.triggerBrowserDownload(doc.url);
        } catch (e: unknown) {
          const err = e as Error;
          onShowSnackbar?.(`Failed to download ${doc.name}: ${err.message}`, 'error');
        }
      }
    }
  };

  const handleAutoEvaluate = async () => {
    setIsEvaluating(true);
    try {
      const documentPaths = (supplier.documents || [])
        .filter((d) => {
          const isValid =
            d.url &&
            d.status !== 'pending' &&
            d.status !== 'corrupt' &&
            d.conversionStatus !== 'pending' &&
            d.conversionStatus !== 'processing';
          return isValid;
        })
        .map((d) => d.url);

      if (documentPaths.length === 0) {
        if (onShowSnackbar) {
          onShowSnackbar('No valid documents available to evaluate.', 'error');
        }
        return;
      }

      const result = await evaluateSupplierClaims(
        rubric.procurementId,
        account?.clientId || '',
        account?.uid || '',
        supplier.id,
        rubric,
        documentPaths,
        currentSettings
      );

      if (result.success && result.evaluations) {
        let matchCount = 0;
        const normalizedEvaluations = result.evaluations.map((ev) => {
          const criteria = rubric.criteria.find((c) => (c.id || c.name) === ev.criteriaId);
          let normalizedScore = ev.score;

          if (criteria) {
            matchCount++;
            const rawScoreStr = String(ev.score).toUpperCase();
            if (criteria.scoringType === 'PASS_FAIL') {
              if (rawScoreStr.includes('PASS') || rawScoreStr === 'TRUE') {
                normalizedScore = true;
              } else {
                normalizedScore = false;
              }
            } else if (criteria.scoringType === 'NUMERIC') {
              // Extract the first number from the string (e.g. "8/10" -> 8)
              const match = String(ev.score).match(/\d+(\.\d+)?/);
              normalizedScore = match ? Number(match[0]) : 0;
            } else if (criteria.scoringType === 'LINGUISTIC') {
              if (rawScoreStr.includes('HIGHLY')) {
                normalizedScore = 'HIGHLY_CONFIDENT';
              } else if (rawScoreStr.includes('MINOR')) {
                normalizedScore = 'MINOR_RESERVATIONS';
              } else if (rawScoreStr.includes('MAJOR')) {
                normalizedScore = 'MAJOR_RESERVATIONS';
              } else if (rawScoreStr.includes('UNACCEPTABLE')) {
                normalizedScore = 'UNACCEPTABLE';
              } else if (rawScoreStr.includes('CONFIDENT')) {
                normalizedScore = 'CONFIDENT';
              } else {
                normalizedScore = 'MINOR_RESERVATIONS'; // Safe fallback
              }
            }
          }

          return {
            ...ev,
            score: normalizedScore,
          };
        });

        setScores(normalizedEvaluations);

        const newOverallComments = result.supplier_summary || overallComments;
        setOverallComments(newOverallComments);

        onSaveEvaluation(normalizedEvaluations, newOverallComments, true);
        if (onShowSnackbar) {
          onShowSnackbar(
            `AI Evaluation Complete. Mapped ${matchCount} out of ${result.evaluations.length} AI scores to ${rubric.criteria.length} rubric criteria.`,
            'success'
          );
        }
      } else {
        if (onShowSnackbar) {
          onShowSnackbar(result.error || 'Failed to generate AI evaluation', 'error');
        }
      }
    } catch (err: unknown) {
      const e = err as Error;
      if (onShowSnackbar) {
        onShowSnackbar(e.message || 'An error occurred during evaluation', 'error');
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleScoreChange = (
    criteriaId: string,
    field: keyof EvaluationScore,
    value: string | number | boolean
  ) => {
    const existingIndex = scores.findIndex((s) => s.criteriaId === criteriaId);

    if (existingIndex >= 0) {
      const newScores = [...scores];
      newScores[existingIndex] = { ...newScores[existingIndex], [field]: value } as EvaluationScore;
      setScores(newScores);
    } else {
      setScores([...scores, { criteriaId, score: '', justification: '', [field]: value }]);
    }
  };

  const getScoreValue = (criteriaId: string): string | number | boolean => {
    return scores.find((s) => s.criteriaId === criteriaId)?.score ?? '';
  };

  const getJustificationValue = (criteriaId: string): string => {
    return scores.find((s) => s.criteriaId === criteriaId)?.justification ?? '';
  };

  const isComplete =
    rubric.criteria.length > 0 &&
    scores.length === rubric.criteria.length &&
    scores.every((s) => s.score !== '' && s.justification.trim() !== '');

  const groupedCriteria = rubric.criteria.reduce(
    (acc, crit) => {
      if (!acc[crit.category]) {
        acc[crit.category] = [];
      }
      acc[crit.category]?.push(crit);
      return acc;
    },
    {} as Record<string, RubricCriteria[]>
  );

  const renderScoringInput = (criteria: RubricCriteria) => {
    switch (criteria.scoringType) {
      case 'PASS_FAIL':
        return (
          <TextField
            select
            fullWidth
            size="small"
            label="Score (Pass/Fail)"
            disabled={isSubmitted || localSubmitted}
            value={String(getScoreValue(criteria.id || criteria.name))}
            onChange={(e) =>
              handleScoreChange(criteria.id || criteria.name, 'score', e.target.value === 'true')
            }
            data-testid={`score-input-${criteria.id || criteria.name}`}
          >
            <MenuItem value="true">Pass</MenuItem>
            <MenuItem value="false">Fail</MenuItem>
          </TextField>
        );
      case 'NUMERIC':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            disabled={isSubmitted || localSubmitted}
            inputProps={{ min: 0, max: 10 }}
            label="Score (0-10)"
            value={getScoreValue(criteria.id || criteria.name)}
            onChange={(e) =>
              handleScoreChange(criteria.id || criteria.name, 'score', Number(e.target.value))
            }
            data-testid={`score-input-${criteria.id || criteria.name}`}
          />
        );
      default:
        return (
          <TextField
            select
            fullWidth
            size="small"
            label="Score (Fuzzy Linguistic)"
            disabled={isSubmitted || localSubmitted}
            value={getScoreValue(criteria.id || criteria.name)}
            onChange={(e) =>
              handleScoreChange(criteria.id || criteria.name, 'score', e.target.value)
            }
            data-testid={`score-input-${criteria.id || criteria.name}`}
          >
            <MenuItem value="HIGHLY_CONFIDENT">Highly Confident</MenuItem>
            <MenuItem value="CONFIDENT">Confident</MenuItem>
            <MenuItem value="MINOR_RESERVATIONS">Minor Reservations</MenuItem>
            <MenuItem value="MAJOR_RESERVATIONS">Major Reservations</MenuItem>
            <MenuItem value="UNACCEPTABLE">Unacceptable</MenuItem>
          </TextField>
        );
    }
  };

  const defaultSettings: ProcurementSettings = {
    compliance: { strictness: 'MEDIUM', customKeywords: [] },
    nliRedTeam: { enabled: true, confidenceThreshold: 'MEDIUM' },
    evaluation: { useFuzzyScoring: true, enabled: true },
    moderation: { reportFormat: 'EXECUTIVE_SUMMARY', tone: 'FORMAL' },
  };

  const currentSettings = procurement?.settings || defaultSettings;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={handleDownloadDocuments}
          sx={{
            bgcolor: 'bidlogiq.brick',
            color: 'white',
            '&:hover': {
              bgcolor: 'bidlogiq.brick',
              filter: 'brightness(0.9)',
            },
            textTransform: 'none',
            py: 0.5,
            px: 2,
          }}
        >
          Download Documents
        </Button>
      </Box>
      <Box>
        <Typography variant="body1" color="textSecondary">
          Review the supplier's submission documents below and score them against the established
          rubric. You can instruct the AI to auto-draft preliminary scores and justifications based
          on the documents, which you must then review and verify before submitting.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 }}>
        {/* Top Side: Interactive Rubric Scorecard */}
        <Card>
          <CardHeader
            sx={{ bgcolor: 'action.hover' }}
            title="Evaluation Scorecard"
            action={
              currentSettings.evaluation.enabled ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={isEvaluating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={handleAutoEvaluate}
                  disabled={
                    isEvaluating || rubric.criteria.length === 0 || isSubmitted || localSubmitted
                  }
                  data-testid="auto-evaluate-btn"
                >
                  {isEvaluating ? 'Evaluating...' : 'Generate Evaluation'}
                </Button>
              ) : (
                <Tooltip title="AI Evaluation is currently disabled by the Panel Chair.">
                  <span>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={<AutoAwesomeIcon />}
                      disabled
                    >
                      AI Disabled
                    </Button>
                  </span>
                </Tooltip>
              )
            }
          />
          <Divider />
          <CardContent>
            {Object.entries(groupedCriteria).map(([category, criteriaList]) => (
              <Box key={category} sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    bgcolor: 'action.hover',
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {category}
                </Typography>
                {criteriaList.map((crit) => (
                  <Box
                    key={crit.id || crit.name}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {crit.name} ({crit.weight}%)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {crit.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>{renderScoringInput(crit)}</Box>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Evaluation Justification"
                      disabled={isSubmitted || localSubmitted}
                      placeholder="Provide detailed reasoning for this score..."
                      value={getJustificationValue(crit.id || crit.name)}
                      onChange={(e) =>
                        handleScoreChange(crit.id || crit.name, 'justification', e.target.value)
                      }
                      data-testid={`justification-input-${crit.id || crit.name}`}
                    />
                  </Box>
                ))}
              </Box>
            ))}

            {rubric.criteria.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No criteria defined in the rubric.
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <TextField
              fullWidth
              multiline
              minRows={3}
              disabled={isSubmitted || localSubmitted}
              label="Overall Comments (Optional)"
              value={overallComments}
              onChange={(e) => setOverallComments(e.target.value)}
              sx={{ mb: 3 }}
              data-testid="overall-comments-input"
            />

            <Button
              variant="contained"
              color={isSubmitted ? 'success' : 'primary'}
              disabled={!isComplete || isSubmitted || localSubmitted}
              onClick={() => setConfirmOpen(true)}
              data-testid="submit-evaluation-btn"
            >
              {isSubmitted
                ? 'Evaluation Submitted'
                : localSubmitted
                  ? 'Submitting...'
                  : 'Submit Evaluation'}
            </Button>
          </CardContent>
        </Card>

        {/* Bottom Side: Document Viewer */}
        <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <CardHeader sx={{ bgcolor: 'action.hover' }} title="Supplier Submission Documents" />
          <Divider />
          <Tabs
            value={activeDocIndex}
            onChange={(_, nv) => setActiveDocIndex(nv)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            {supplier.documents.map((doc) => (
              <Tab key={doc.id} label={doc.name} />
            ))}
          </Tabs>
          <CardContent
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              p: 2,
            }}
          >
            {supplier.documents.length > 0 && supplier.documents[activeDocIndex] ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Interactive Document Viewer Disabled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please use the "Download Documents" button above to review the supplier's
                  submission.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No documents attached.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Dialog */}
      <BaseModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Submission"
        actions={
          <>
            <Button onClick={() => setConfirmOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmSubmit} color="primary" variant="contained" autoFocus>
              Submit
            </Button>
          </>
        }
      >
        <DialogContentText>
          Are you sure you want to submit your evaluation for {supplier.name}? This action cannot be
          undone and will lock in your scores.
        </DialogContentText>
      </BaseModal>
    </Box>
  );
};

export default SupplierAssessmentMatrix;
